import type { Rgb } from '@/lib/hero-visual/color'

import type {
    HeroColorName,
    HeroCurrent,
    HeroGlassShape,
    HeroLayoutFrame,
    HeroQualityTier,
    HeroVisualConfig
} from '@/lib/hero-visual/types'

import { hexToRgb, mixRgb, rgba } from '@/lib/hero-visual/color'
import { createNoise, createRandom } from '@/lib/hero-visual/noise'

// Animation engine of the hero visual. It draws two canvases, one behind the
// glass shapes and one in front of them, and moves the glass elements so the
// canvas refraction inside each shape stays aligned with it. Browser-only:
// create it from an effect after mount.

type Canvas2D = CanvasRenderingContext2D

export type HeroVisualElements = {
    root: HTMLElement
    back: HTMLCanvasElement
    front: HTMLCanvasElement
    glass: HTMLElement[]
}

export type HeroVisualHandle = {
    destroy: () => void
}

type Layout = {
    width: number
    height: number
    pixelWidth: number
    pixelHeight: number
    scaleX: number
    scaleY: number
    tier: HeroQualityTier
    portrait: boolean
    frame: HeroLayoutFrame
    fx: number
    fy: number
    r: number
    unit: number
    shift: number
    clearFrom: number
    clearTo: number
    x0: number
    x1: number
    samples: number
}

type Pulse = {
    line: number
    head: number
    speed: number
    length: number
    wait: number
}

type CurrentLine = {
    width: number
    thickness: number
    breathSeed: number
    variant: number
    points: Float32Array
}

type Current = {
    def: HeroCurrent
    seeds: {
        center: number
        spread: number
        twist: number
        jitter: number
    }
    lines: CurrentLine[]
    count: number
    gradients: CanvasGradient[]
    pulses: Pulse[]
}

type NetNode = {
    x: number
    y: number
    vx: number
    vy: number
    z: number
    seed: number
    delay: number
    alpha: number
    sx: number
    sy: number
}

type Signal = {
    a: number
    b: number
    p: number
    duration: number
    wait: number
}

type Ping = {
    node: number
    p: number
    duration: number
    wait: number
}

type Glass = {
    def: HeroGlassShape
    el: HTMLElement
    enabled: boolean
    seeds: [number, number, number, number]
    delay: number
    w: number
    h: number
    radius: number
    cx: number
    cy: number
    rot: number
    scale: number
    opacity: number
    written: {
        opacity: number
        rim: number
        specX: number
        specY: number
        sheen: number
    }
}

const TAU = Math.PI * 2
const DEG = Math.PI / 180

// Light falls from the upper left. The conic angle is measured clockwise from the top.
const LIGHT_X = -0.6
const LIGHT_Y = -0.8
const LIGHT_ANGLE = 323

const MIN_CLEAR = 0.05
const INTRO_SECONDS = 2.6
const SPRITE_SIZE = 128
const LINK_BUCKETS = 8

const clamp = (value: number, min: number, max: number): number => (value < min ? min : value > max ? max : value)

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

const smoothstep = (edge0: number, edge1: number, value: number): number => {
    const t = clamp((value - edge0) / (edge1 - edge0), 0, 1)

    return t * t * (3 - 2 * t)
}

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3)

const easeInOutSine = (t: number): number => 0.5 - 0.5 * Math.cos(Math.PI * t)

const between = (min: number, max: number): number => min + (max - min) * Math.random()

const createGlowSprite = (color: Rgb): HTMLCanvasElement => {
    const canvas = document.createElement('canvas')
    canvas.width = SPRITE_SIZE
    canvas.height = SPRITE_SIZE

    const ctx = canvas.getContext('2d')
    if (!ctx) return canvas

    const half = SPRITE_SIZE / 2
    const gradient = ctx.createRadialGradient(half, half, 0, half, half, half)
    gradient.addColorStop(0, rgba(color, 1))
    gradient.addColorStop(0.16, rgba(color, 0.62))
    gradient.addColorStop(0.38, rgba(color, 0.22))
    gradient.addColorStop(0.66, rgba(color, 0.06))
    gradient.addColorStop(1, rgba(color, 0))

    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, SPRITE_SIZE, SPRITE_SIZE)

    return canvas
}

// Smooth curve through the sample points, using the midpoints as anchors
const traceCurve = (ctx: Canvas2D, points: Float32Array, count: number): void => {
    ctx.beginPath()
    ctx.moveTo(points[0], points[1])

    for (let s = 1; s < count - 1; s++) {
        const x = points[s * 2]
        const y = points[s * 2 + 1]
        ctx.quadraticCurveTo(x, y, (x + points[s * 2 + 2]) * 0.5, (y + points[s * 2 + 3]) * 0.5)
    }

    ctx.lineTo(points[count * 2 - 2], points[count * 2 - 1])
}

// Same outline as a CSS box with this border radius: a circle, capsule or rounded rectangle
const traceRoundedRect = (ctx: Canvas2D, x: number, y: number, w: number, h: number, radius: number): void => {
    const r = Math.max(0, Math.min(radius, w / 2, h / 2))

    ctx.moveTo(x + r, y)
    ctx.lineTo(x + w - r, y)
    ctx.arc(x + w - r, y + r, r, -Math.PI / 2, 0)
    ctx.lineTo(x + w, y + h - r)
    ctx.arc(x + w - r, y + h - r, r, 0, Math.PI / 2)
    ctx.lineTo(x + r, y + h)
    ctx.arc(x + r, y + h - r, r, Math.PI / 2, Math.PI)
    ctx.lineTo(x, y + r)
    ctx.arc(x + r, y + r, r, Math.PI, Math.PI * 1.5)
    ctx.closePath()
}

export const createHeroVisual = (elements: HeroVisualElements, config: HeroVisualConfig): HeroVisualHandle => {
    const { root, back, front } = elements
    const snap = document.createElement('canvas')
    const backCtx = back.getContext('2d')
    const frontCtx = front.getContext('2d')
    const snapCtx = snap.getContext('2d')

    if (!backCtx || !frontCtx || !snapCtx) {
        return { destroy: () => undefined }
    }

    const random = createRandom(config.seed)
    const { noise2D, noise3D } = createNoise(random)
    const seeded = (min: number, max: number): number => min + (max - min) * random()

    const palette: Record<HeroColorName, Rgb> = {
        cyan: hexToRgb(config.colors.cyan),
        teal: hexToRgb(config.colors.teal),
        ice: hexToRgb(config.colors.ice),
        frost: hexToRgb(config.colors.frost)
    }

    const sprites: Record<HeroColorName, HTMLCanvasElement> = {
        cyan: createGlowSprite(palette.cyan),
        teal: createGlowSprite(palette.teal),
        ice: createGlowSprite(palette.ice),
        frost: createGlowSprite(palette.frost)
    }

    const frostFill = rgba(palette.frost, 1)
    const linkStroke = rgba(mixRgb(palette.ice, palette.cyan, 0.45), 1)
    const pingStroke = rgba(palette.cyan, 1)

    let time = config.startTime
    let introClock = 0
    let running = false
    let raf = 0
    let last = 0
    let inView = true
    let reduced = false
    let needsLayout = false
    let deviceRatio = window.devicePixelRatio || 1
    let quality = 0
    let frameAverage = 1 / 60
    let slowSeconds = 0

    const pointer = { x: 0, y: 0, tx: 0, ty: 0 }
    const view = { x: 0, y: 0 }
    const ring = { angle: 0.85 }
    const swaySeed = seeded(0, 100)
    const ringSeed = seeded(0, 100)
    const ambientSeeds = config.ambient.map(() => seeded(0, 100))
    const bokehSeeds = config.bokeh.map(() => seeded(0, 100))

    // Currents: ribbons of lines that bend, pinch and twist like ocean currents

    const currents: Current[] = config.currents.map((def) => ({
        def,
        seeds: {
            center: seeded(0, 100),
            spread: seeded(0, 100),
            twist: seeded(0, 100),
            jitter: seeded(0, 100)
        },
        lines: Array.from({ length: def.lines }, (_, index) => ({
            width: def.width * seeded(0.72, 1.28),
            thickness: def.thickness * seeded(-1, 1),
            breathSeed: seeded(0, 100),
            variant: index % 2,
            points: new Float32Array(0)
        })),
        count: def.lines,
        gradients: [],
        pulses: Array.from({ length: def.pulses }, () => ({
            line: 0,
            head: 0,
            speed: 0,
            length: 0,
            wait: 0
        }))
    }))

    // Network: drifting particles that link and unlink as they move

    const bounds = root.getBoundingClientRect()
    const startFrame = bounds.width / Math.max(1, bounds.height) < 1 ? config.layout.portrait : config.layout.landscape
    const maxNodes = Math.max(config.network.count.high, config.network.count.medium, config.network.count.low)

    const nodes: NetNode[] = Array.from({ length: maxNodes }, () => {
        const angle = seeded(0, TAU)
        const distance = Math.pow(seeded(0, 1), 0.62)

        return {
            x: startFrame.networkX + Math.cos(angle) * distance * startFrame.networkWidth,
            y: startFrame.networkY + Math.sin(angle) * distance * startFrame.networkHeight,
            vx: 0,
            vy: 0,
            z: Math.pow(seeded(0, 1), 0.85),
            seed: seeded(0, 100),
            delay: seeded(0.2, 1.8),
            alpha: 0,
            sx: 0,
            sy: 0
        }
    })

    const pairTotal = (maxNodes * (maxNodes - 1)) / 2
    const pairSpeed = new Float32Array(pairTotal)
    const pairPhase = new Float32Array(pairTotal)

    for (let k = 0; k < pairTotal; k++) {
        pairSpeed[k] = seeded(0.04, 0.13)
        pairPhase[k] = seeded(0, TAU)
    }

    const pairIndex = (i: number, j: number): number => (i * (2 * maxNodes - i - 1)) / 2 + (j - i - 1)

    const linkA = new Uint16Array(pairTotal)
    const linkB = new Uint16Array(pairTotal)
    const linkStrength = new Float32Array(pairTotal)
    const pushX = new Float32Array(maxNodes)
    const pushY = new Float32Array(maxNodes)

    let linkCount = 0
    let activeNodes = config.network.count.high
    let linkDistance = config.network.linkDistance

    const hubOrder = nodes
        .slice(0, config.network.count.low)
        .map((node, index) => ({
            index,
            score: Math.abs(node.z - 0.65) + Math.hypot(node.x - startFrame.networkX, node.y - startFrame.networkY) * 0.35
        }))
        .sort((a, b) => a.score - b.score)

    const pings: Ping[] = hubOrder.slice(0, config.network.hubs).map((hub, index) => ({
        node: hub.index,
        p: 1,
        duration: 0,
        wait: 2.5 + index * 3.5 + Math.random() * 2
    }))

    const hubs = new Set(pings.map((ping) => ping.node))

    const signals: Signal[] = Array.from({ length: config.network.signals }, (_, index) => ({
        a: 0,
        b: 0,
        p: 1,
        duration: 0,
        wait: 2 + index * 1.7 + Math.random() * 2
    }))

    // Glass: floating shapes, larger ones appear first

    const byArea = config.glass
        .map((def, index) => ({ index, area: def.width * def.height }))
        .sort((a, b) => b.area - a.area)

    const glass: Glass[] = []

    config.glass.forEach((def, index) => {
        const el = elements.glass.find((element) => element.dataset.glass === def.id)
        if (!el) return

        const rank = byArea.findIndex((entry) => entry.index === index)

        glass.push({
            def,
            el,
            enabled: true,
            seeds: [seeded(0, 100), seeded(0, 100), seeded(0, 100), seeded(0, 100)],
            delay: 0.35 + rank * 0.26,
            w: 0,
            h: 0,
            radius: 0,
            cx: 0,
            cy: 0,
            rot: def.rotation,
            scale: 1,
            opacity: 0,
            written: {
                opacity: -1,
                rim: Number.NaN,
                specX: Number.NaN,
                specY: Number.NaN,
                sheen: Number.NaN
            }
        })
    })

    const orb = glass.find((entry) => entry.def.kind === 'orb')

    // Layout

    const computeLayout = (): Layout => {
        const rect = root.getBoundingClientRect()
        const width = Math.max(1, rect.width)
        const height = Math.max(1, rect.height)
        const portrait = width / height < 1
        const frame = portrait ? config.layout.portrait : config.layout.landscape
        const tier: HeroQualityTier = width >= config.quality.mediumBelow ? 'high' : width >= config.quality.lowBelow ? 'medium' : 'low'
        const budget = Math.sqrt(config.quality.pixelBudget[tier] / (width * height))

        let ratio = clamp(Math.min(deviceRatio, config.quality.maxPixelRatio[tier], budget), 0.75, 3)
        if (quality >= 2) ratio = Math.min(ratio, 1)

        const pixelWidth = Math.max(1, Math.round(width * ratio))
        const pixelHeight = Math.max(1, Math.round(height * ratio))
        const r = Math.max(40, Math.min(width * frame.radiusOfWidth, height * frame.radiusOfHeight))
        const x0 = portrait ? -0.06 * width : 0.03 * width
        const x1 = portrait ? 1.06 * width : 1.03 * width
        const step = Math.max(9, width / 150)

        return {
            width,
            height,
            pixelWidth,
            pixelHeight,
            scaleX: pixelWidth / width,
            scaleY: pixelHeight / height,
            tier,
            portrait,
            frame,
            fx: width * frame.focusX,
            fy: height * frame.focusY,
            r,
            unit: clamp(r / 540, 0.7, 1.35),
            shift: config.parallax.shift * r,
            clearFrom: frame.clearFrom * (portrait ? height : width),
            clearTo: frame.clearTo * (portrait ? height : width),
            x0,
            x1,
            samples: Math.round(clamp((x1 - x0) / step, 36, 170))
        }
    }

    let layout: Layout = computeLayout()

    // How visible content may be at a position: low over the title area, full over the visual
    const clearanceAt = (value: number): number => lerp(MIN_CLEAR, 1, smoothstep(layout.clearFrom, layout.clearTo, value))

    const clearance = (x: number, y: number): number => clearanceAt(layout.portrait ? y : x)

    const createCurrentGradient = (ctx: Canvas2D, start: Rgb, end: Rgb): CanvasGradient => {
        const gradient = ctx.createLinearGradient(layout.x0, 0, layout.x1, 0)
        const stops = 12

        for (let i = 0; i <= stops; i++) {
            const p = i / stops
            const edge = smoothstep(0, 0.04, p) * (1 - smoothstep(0.97, 1, p))
            const visible = layout.portrait ? 1 : clearanceAt(lerp(layout.x0, layout.x1, p))
            gradient.addColorStop(p, rgba(mixRgb(start, end, p), visible * edge))
        }

        return gradient
    }

    const applyLayout = (): void => {
        layout = computeLayout()

        for (const canvas of [back, front, snap]) {
            if (canvas.width !== layout.pixelWidth) canvas.width = layout.pixelWidth
            if (canvas.height !== layout.pixelHeight) canvas.height = layout.pixelHeight
        }

        root.dataset.orientation = layout.portrait ? 'portrait' : 'landscape'

        const lineFactor = layout.tier === 'high' ? 1 : layout.tier === 'medium' ? 0.86 : 0.72

        for (const current of currents) {
            const { def } = current
            const ctx = def.layer === 'front' ? frontCtx : backCtx
            const [first, second] = def.colors

            current.count = clamp(Math.round(def.lines * lineFactor), Math.min(2, def.lines), def.lines)
            current.gradients = [
                createCurrentGradient(ctx, palette[first], palette[second]),
                createCurrentGradient(ctx, palette[second], palette[first])
            ]

            for (const line of current.lines) {
                if (line.points.length !== layout.samples * 2) line.points = new Float32Array(layout.samples * 2)
            }

            for (const pulse of current.pulses) {
                pulse.line = Math.min(pulse.line, current.count - 1)
            }
        }

        activeNodes = config.network.count[layout.tier]
        linkDistance = config.network.linkDistance * Math.sqrt(config.network.count.high / activeNodes)

        for (const entry of glass) {
            entry.enabled = entry.def.smallScreens || layout.tier !== 'low'
            entry.w = entry.def.width * layout.r
            entry.h = entry.def.height * layout.r
            entry.radius = entry.def.radius * Math.min(entry.w, entry.h)
            entry.el.style.display = entry.enabled ? '' : 'none'
            entry.el.style.width = `${entry.w.toFixed(2)}px`
            entry.el.style.height = `${entry.h.toFixed(2)}px`
            entry.el.style.borderRadius = `${entry.radius.toFixed(2)}px`
            entry.written.rim = Number.NaN
            entry.written.specX = Number.NaN
            entry.written.specY = Number.NaN
            entry.written.sheen = Number.NaN
        }
    }

    // Parallax: the view follows the mouse and also drifts on its own

    const updateView = (still: boolean): void => {
        const swayX = noise2D(swaySeed, time * 0.045)
        const swayY = noise2D(swaySeed + 9.3, time * 0.038)

        view.x = (still ? 0 : pointer.x) * config.parallax.pointer + swayX * config.parallax.sway
        view.y = (still ? 0 : pointer.y) * config.parallax.pointer + swayY * config.parallax.sway
    }

    const offsetX = (depth: number): number => -view.x * depth * layout.shift

    const offsetY = (depth: number): number => -view.y * depth * layout.shift * 0.75

    const drawSprite = (ctx: Canvas2D, color: HeroColorName, x: number, y: number, size: number, alpha: number): void => {
        if (alpha <= 0.002 || size <= 0.5) return

        ctx.globalAlpha = Math.min(1, alpha)
        ctx.drawImage(sprites[color], x - size / 2, y - size / 2, size, size)
    }

    // Ambient glow

    const drawAmbient = (ctx: Canvas2D, reveal: number): void => {
        const dx = offsetX(0.12)
        const dy = offsetY(0.12)

        ctx.globalAlpha = 1

        config.ambient.forEach((glow, index) => {
            const seed = ambientSeeds[index]
            const cx = layout.fx + (glow.x + glow.drift * noise2D(seed, time * glow.speed)) * layout.r + dx
            const cy = layout.fy + (glow.y + glow.drift * noise2D(seed + 31.7, time * glow.speed * 1.17)) * layout.r + dy
            const radius = glow.size * layout.r * (1 + 0.08 * noise2D(seed + 63.1, time * glow.speed * 0.8))
            const alpha = glow.alpha * reveal
            const color = palette[glow.color]
            const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius)

            gradient.addColorStop(0, rgba(color, alpha))
            gradient.addColorStop(0.22, rgba(color, alpha * 0.8))
            gradient.addColorStop(0.45, rgba(color, alpha * 0.46))
            gradient.addColorStop(0.7, rgba(color, alpha * 0.16))
            gradient.addColorStop(1, rgba(color, 0))

            ctx.fillStyle = gradient
            ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2)
        })
    }

    // Currents

    const computeCurrent = (current: Current): void => {
        const { def, seeds } = current
        const { r, fx, fy, x0, x1, samples } = layout
        const span = x1 - x0

        for (let s = 0; s < samples; s++) {
            const x = x0 + (span * s) / (samples - 1)
            const u = (x - fx) / r
            const wave = u * def.waviness - time * def.flow
            const bend = noise2D(wave + seeds.center, time * def.speed)
                + 0.5 * noise2D(wave * 2.03 + seeds.center + 17.1, time * def.speed * 1.9 + 3.7)
            const center = def.offsetY + def.tilt * u + (def.amplitude * bend) / 1.5
            const spread = def.spread * (0.62 + 0.38 * noise2D(u * 0.45 - time * def.flow * 0.6 + seeds.spread, time * def.speed * 0.7))
            const theta = Math.PI * (def.twist * u + 0.55 * noise2D(u * 0.3 + seeds.twist, time * def.speed * 0.5))
            const across = Math.cos(theta) * spread
            const edgeOn = Math.sin(theta) * spread

            for (let i = 0; i < current.count; i++) {
                const line = current.lines[i]
                const k = current.count > 1 ? i / (current.count - 1) - 0.5 : 0
                const jitter = def.jitter * noise2D(u * 1.1 + i * 3.17 + seeds.jitter, time * def.speed * 1.4 + i * 0.71)

                line.points[s * 2] = x
                line.points[s * 2 + 1] = fy + (center + across * k + edgeOn * line.thickness + jitter) * r
            }
        }
    }

    const resetPulse = (pulse: Pulse, current: Current, first: boolean): void => {
        pulse.line = Math.floor(Math.random() * current.count)
        pulse.speed = between(0.045, 0.075)
        pulse.length = between(0.05, 0.09)
        pulse.head = layout.portrait ? 0.02 : 0.22
        pulse.wait = first ? between(1.2, 4.5) : between(2.5, 7.5)
    }

    const updatePulses = (dt: number): void => {
        for (const current of currents) {
            for (const pulse of current.pulses) {
                if (pulse.wait > 0) {
                    pulse.wait -= dt
                    continue
                }

                pulse.head += pulse.speed * dt
                if (pulse.head - pulse.length > 1) resetPulse(pulse, current, false)
            }
        }
    }

    const drawPulse = (ctx: Canvas2D, current: Current, pulse: Pulse, reveal: number): void => {
        if (pulse.wait > 0) return

        const lastIndex = layout.samples - 1
        const end = Math.min(lastIndex, Math.ceil(pulse.head * lastIndex))
        const start = Math.max(0, Math.floor((pulse.head - pulse.length) * lastIndex))
        if (end - start < 2) return

        const points = current.lines[Math.min(pulse.line, current.count - 1)].points
        const tx = points[start * 2]
        const ty = points[start * 2 + 1]
        const hx = points[end * 2]
        const hy = points[end * 2 + 1]
        const begin = layout.portrait ? 0.02 : 0.22
        const envelope = smoothstep(begin, begin + 0.1, pulse.head) * (1 - smoothstep(0.88, 1, pulse.head)) * clearance(hx, hy) * reveal
        if (envelope <= 0.01) return

        ctx.beginPath()
        ctx.moveTo(tx, ty)

        for (let s = start + 1; s <= end; s++) {
            ctx.lineTo(points[s * 2], points[s * 2 + 1])
        }

        const gradient = ctx.createLinearGradient(tx, ty, hx, hy)
        gradient.addColorStop(0, rgba(palette.cyan, 0))
        gradient.addColorStop(0.7, rgba(palette.cyan, 0.5))
        gradient.addColorStop(1, rgba(palette.frost, 1))

        ctx.strokeStyle = gradient
        ctx.globalAlpha = 0.24 * envelope
        ctx.lineWidth = 6 * layout.unit
        ctx.stroke()
        ctx.globalAlpha = 0.85 * envelope
        ctx.lineWidth = 1.5
        ctx.stroke()

        drawSprite(ctx, 'cyan', hx, hy, 16 * layout.unit, 0.45 * envelope)
    }

    const drawCurrent = (ctx: Canvas2D, current: Current, still: boolean, reveal: number): void => {
        const { def } = current

        ctx.save()
        ctx.translate(offsetX(def.depth), offsetY(def.depth))

        for (let i = 0; i < current.count; i++) {
            const line = current.lines[i]
            const breath = 0.62 + 0.38 * (0.5 + 0.5 * noise2D(line.breathSeed, time * 0.07))
            const alpha = def.alpha * breath * reveal
            if (alpha <= 0.002) continue

            traceCurve(ctx, line.points, layout.samples)
            ctx.strokeStyle = current.gradients[line.variant]

            if (quality < 1) {
                ctx.globalAlpha = alpha * def.glow * 0.07
                ctx.lineWidth = line.width * 7
                ctx.stroke()
            }

            ctx.globalAlpha = alpha * def.glow * 0.18
            ctx.lineWidth = line.width * 2.8
            ctx.stroke()
            ctx.globalAlpha = alpha
            ctx.lineWidth = line.width
            ctx.stroke()
        }

        if (!still) {
            for (const pulse of current.pulses) {
                drawPulse(ctx, current, pulse, reveal)
            }
        }

        ctx.restore()
    }

    // Network

    const updateNodes = (dt: number): void => {
        const { network } = config
        const { frame } = layout
        const flowTime = time * network.flowSpeed
        const e = 0.04
        const ease = Math.min(1, dt * 1.1)

        for (let i = 0; i < activeNodes; i++) {
            const node = nodes[i]
            const px = node.x * network.flowScale
            const py = node.y * network.flowScale
            const speed = network.speed * (0.6 + 0.5 * node.z) * 0.3

            // Curl of a noise field: a swirling flow that never bunches particles together
            let vx = ((noise3D(px, py + e, flowTime) - noise3D(px, py - e, flowTime)) / (2 * e)) * speed
            let vy = ((noise3D(px - e, py, flowTime) - noise3D(px + e, py, flowTime)) / (2 * e)) * speed

            const dx = node.x - frame.networkX
            const dy = node.y - frame.networkY
            const reach = Math.sqrt((dx / frame.networkWidth) ** 2 + (dy / frame.networkHeight) ** 2)

            if (reach > 0.82) {
                const length = Math.sqrt(dx * dx + dy * dy) || 1
                const pull = (reach - 0.82) * network.speed * 3.2
                vx -= (dx / length) * pull
                vy -= (dy / length) * pull
            }

            vx += pushX[i]
            vy += pushY[i]

            node.vx += (vx - node.vx) * ease
            node.vy += (vy - node.vy) * ease
            node.x += node.vx * dt
            node.y += node.vy * dt
        }
    }

    // Links fade with distance, depth and a slow affinity of their own, so pairs
    // drift apart and reconnect at unpredictable moments
    const pairStrength = (i: number, j: number, distance: number): number => {
        if (distance >= linkDistance) return 0

        const closeness = 1 - distance / linkDistance
        const near = closeness * closeness * (3 - 2 * closeness)
        const k = pairIndex(i, j)
        const affinity = smoothstep(0.22, 0.78, 0.5 + 0.5 * Math.sin(time * pairSpeed[k] + pairPhase[k]))
        const depth = 1 - smoothstep(0.38, 0.62, Math.abs(nodes[i].z - nodes[j].z))

        return near * affinity * depth
    }

    const computeLinks = (): void => {
        const repel = 0.11
        const repelForce = config.network.speed * 0.8

        linkCount = 0
        pushX.fill(0)
        pushY.fill(0)

        for (let i = 0; i < activeNodes; i++) {
            const a = nodes[i]

            for (let j = i + 1; j < activeNodes; j++) {
                const b = nodes[j]
                const dx = b.x - a.x
                const dy = b.y - a.y
                const distance = Math.sqrt(dx * dx + dy * dy)

                if (distance < repel && distance > 0.0001) {
                    const force = ((repel - distance) / repel) * repelForce
                    const fx = (dx / distance) * force
                    const fy = (dy / distance) * force
                    pushX[i] -= fx
                    pushY[i] -= fy
                    pushX[j] += fx
                    pushY[j] += fy
                }

                const strength = pairStrength(i, j, distance)

                if (strength > 0.01) {
                    linkA[linkCount] = i
                    linkB[linkCount] = j
                    linkStrength[linkCount] = strength
                    linkCount++
                }
            }
        }
    }

    const positionNodes = (still: boolean): void => {
        for (let i = 0; i < activeNodes; i++) {
            const node = nodes[i]
            const depth = 0.25 + 0.9 * node.z
            const appear = still ? 1 : smoothstep(node.delay, node.delay + 1.4, introClock)
            const twinkle = 0.72 + 0.28 * noise2D(node.seed, time * 0.22)

            node.sx = layout.fx + node.x * layout.r + offsetX(depth)
            node.sy = layout.fy + node.y * layout.r + offsetY(depth)
            node.alpha = (0.32 + 0.6 * node.z) * twinkle * appear * clearance(node.sx, node.sy)
        }
    }

    const drawLinks = (ctx: Canvas2D): void => {
        if (linkCount === 0) return

        ctx.strokeStyle = linkStroke
        ctx.lineWidth = 0.75 * layout.unit

        for (let bucket = 0; bucket < LINK_BUCKETS; bucket++) {
            const low = bucket / LINK_BUCKETS
            const high = (bucket + 1) / LINK_BUCKETS
            let any = false

            ctx.beginPath()

            for (let l = 0; l < linkCount; l++) {
                const a = nodes[linkA[l]]
                const b = nodes[linkB[l]]
                const value = linkStrength[l] * Math.min(a.alpha, b.alpha)
                if (value < 0.015 || value <= low || value > high) continue

                ctx.moveTo(a.sx, a.sy)
                ctx.lineTo(b.sx, b.sy)
                any = true
            }

            if (!any) continue

            ctx.globalAlpha = ((low + high) / 2) * config.network.linkAlpha
            ctx.stroke()
        }
    }

    const drawNodes = (ctx: Canvas2D): void => {
        const { unit } = layout

        for (let i = 0; i < activeNodes; i++) {
            const node = nodes[i]
            if (node.alpha <= 0.01) continue

            const hub = hubs.has(i)
            const glowSize = (8 + 14 * node.z) * unit * (hub ? 1.6 : 1)

            drawSprite(ctx, node.z > 0.55 ? 'cyan' : 'ice', node.sx, node.sy, glowSize, node.alpha * (hub ? 0.7 : 0.5))

            ctx.globalAlpha = Math.min(1, node.alpha * 1.1)
            ctx.fillStyle = frostFill
            ctx.beginPath()
            ctx.arc(node.sx, node.sy, (0.8 + 1.5 * node.z) * unit * (hub ? 1.3 : 1), 0, TAU)
            ctx.fill()
        }
    }

    const updateSignals = (dt: number): void => {
        for (const signal of signals) {
            if (signal.wait > 0) {
                signal.wait -= dt
                if (signal.wait > 0) continue

                let pick = -1

                for (let attempt = 0; attempt < 6 && linkCount > 0; attempt++) {
                    const l = Math.floor(Math.random() * linkCount)

                    if (linkStrength[l] > 0.35) {
                        pick = l
                        break
                    }
                }

                if (pick < 0) {
                    signal.wait = 0.8
                    continue
                }

                const forward = Math.random() < 0.5
                signal.a = forward ? linkA[pick] : linkB[pick]
                signal.b = forward ? linkB[pick] : linkA[pick]
                signal.p = 0
                signal.duration = between(2.6, 4.6)
                continue
            }

            signal.p += dt / signal.duration

            if (signal.p >= 1) {
                signal.p = 1
                signal.wait = between(1.5, 5)
            }
        }
    }

    const drawSignals = (ctx: Canvas2D): void => {
        for (const signal of signals) {
            if (signal.wait > 0 || signal.p >= 1) continue
            if (signal.a >= activeNodes || signal.b >= activeNodes) continue

            const a = nodes[signal.a]
            const b = nodes[signal.b]
            const strength = pairStrength(Math.min(signal.a, signal.b), Math.max(signal.a, signal.b), Math.hypot(b.x - a.x, b.y - a.y))
            const visibility = Math.sin(Math.PI * signal.p) * strength * Math.min(a.alpha, b.alpha)
            if (visibility <= 0.01) continue

            const p = easeInOutSine(signal.p)
            const x = lerp(a.sx, b.sx, p)
            const y = lerp(a.sy, b.sy, p)

            drawSprite(ctx, 'cyan', x, y, 12 * layout.unit, 0.8 * visibility)

            ctx.globalAlpha = Math.min(1, 1.2 * visibility)
            ctx.fillStyle = frostFill
            ctx.beginPath()
            ctx.arc(x, y, 1.2 * layout.unit, 0, TAU)
            ctx.fill()
        }
    }

    // Sonar pings: a soft ring that rises from a hub now and then

    const updatePings = (dt: number): void => {
        for (const ping of pings) {
            if (ping.wait > 0) {
                ping.wait -= dt

                if (ping.wait <= 0) {
                    ping.p = 0
                    ping.duration = between(4, 6)
                }

                continue
            }

            ping.p += dt / ping.duration

            if (ping.p >= 1) {
                ping.p = 1
                ping.wait = between(7, 15)
            }
        }
    }

    const drawPings = (ctx: Canvas2D): void => {
        ctx.strokeStyle = pingStroke
        ctx.lineWidth = layout.unit

        for (const ping of pings) {
            if (ping.wait > 0 || ping.p >= 1) continue

            const node = nodes[ping.node]
            const radius = (0.035 + 0.27 * easeOutCubic(ping.p)) * layout.r
            const alpha = 0.3 * Math.pow(1 - ping.p, 1.6) * smoothstep(0, 0.06, ping.p) * node.alpha
            if (alpha <= 0.005) continue

            ctx.globalAlpha = alpha
            ctx.beginPath()
            ctx.arc(node.sx, node.sy, radius, 0, TAU)
            ctx.stroke()

            if (ping.p > 0.14) {
                const echo = (ping.p - 0.14) / 0.86

                ctx.globalAlpha = alpha * 0.5
                ctx.beginPath()
                ctx.arc(node.sx, node.sy, (0.035 + 0.27 * easeOutCubic(echo)) * layout.r * 0.82, 0, TAU)
                ctx.stroke()
            }
        }
    }

    // Orbit ring around the orb: the far half passes behind the glass, the near half in front

    const drawRing = (ctx: Canvas2D, half: 'back' | 'front'): void => {
        if (!orb || !orb.enabled || orb.opacity <= 0.01) return

        const settings = config.ring
        const radiusX = orb.w * 0.5 * orb.scale * settings.radius
        const radiusY = radiusX * (settings.ratio + settings.ratioDrift * noise2D(ringSeed, time * settings.speed))
        const tilt = (settings.tilt + settings.tiltDrift * noise2D(ringSeed + 7.7, time * settings.speed * 0.8)) * DEG
        const cos = Math.cos(tilt)
        const sin = Math.sin(tilt)
        const strength = settings.alpha * orb.opacity * (half === 'front' ? 1 : 0.5)
        const gradient = ctx.createLinearGradient(orb.cx - radiusX * cos, orb.cy - radiusX * sin, orb.cx + radiusX * cos, orb.cy + radiusX * sin)

        gradient.addColorStop(0, rgba(palette.teal, 0))
        gradient.addColorStop(0.18, rgba(palette.teal, 0.35))
        gradient.addColorStop(0.5, rgba(palette.ice, 1))
        gradient.addColorStop(0.82, rgba(palette.cyan, 0.35))
        gradient.addColorStop(1, rgba(palette.cyan, 0))

        ctx.beginPath()

        if (half === 'front') {
            ctx.ellipse(orb.cx, orb.cy, radiusX, radiusY, tilt, 0, Math.PI)
        }
        else {
            ctx.ellipse(orb.cx, orb.cy, radiusX, radiusY, tilt, Math.PI, TAU)
        }

        ctx.strokeStyle = gradient
        ctx.globalAlpha = strength * 0.18
        ctx.lineWidth = settings.width * 6 * layout.unit
        ctx.stroke()
        ctx.globalAlpha = strength
        ctx.lineWidth = settings.width * layout.unit
        ctx.stroke()

        const lift = Math.sin(ring.angle)
        if ((lift >= 0) !== (half === 'front')) return

        const ex = radiusX * Math.cos(ring.angle)
        const ey = radiusY * lift
        const x = orb.cx + ex * cos - ey * sin
        const y = orb.cy + ex * sin + ey * cos
        const visibility = lerp(0.35, 1, smoothstep(-0.3, 0.3, lift)) * orb.opacity

        drawSprite(ctx, 'cyan', x, y, 18 * layout.unit, 0.55 * visibility)

        ctx.globalAlpha = 0.95 * visibility
        ctx.fillStyle = frostFill
        ctx.beginPath()
        ctx.arc(x, y, 1.6 * layout.unit, 0, TAU)
        ctx.fill()
    }

    const drawBokeh = (ctx: Canvas2D, reveal: number): void => {
        config.bokeh.forEach((bokeh, index) => {
            const seed = bokehSeeds[index]
            const x = layout.fx + (bokeh.x + bokeh.drift * noise2D(seed, time * bokeh.speed)) * layout.r + offsetX(bokeh.depth)
            const y = layout.fy + (bokeh.y + bokeh.drift * noise2D(seed + 5.1, time * bokeh.speed * 1.2)) * layout.r + offsetY(bokeh.depth)
            const size = bokeh.size * layout.r * 2 * (1 + 0.1 * noise2D(seed + 9.7, time * bokeh.speed))

            drawSprite(ctx, bokeh.color, x, y, size, bokeh.alpha * reveal * clearance(x, y))
        })
    }

    // Glass

    const updateGlass = (still: boolean): void => {
        const { r, fx, fy } = layout

        for (const entry of glass) {
            if (!entry.enabled) continue

            const { def, seeds } = entry
            const appear = still ? 1 : easeOutCubic(clamp((introClock - entry.delay) / 1.8, 0, 1))
            const floatX = def.drift * noise2D(seeds[0], time * def.speed) * r
            const floatY = def.drift * 1.3 * noise2D(seeds[1], time * def.speed * 1.13) * r + (1 - appear) * 0.08 * r

            entry.rot = def.rotation + def.sway * noise2D(seeds[2], time * def.speed * 0.83)
            entry.scale = (1 + 0.014 * noise2D(seeds[3], time * def.speed * 0.71)) * (0.94 + 0.06 * appear)
            entry.cx = fx + def.x * r + floatX + offsetX(def.depth)
            entry.cy = fy + def.y * r + floatY + offsetY(def.depth)
            entry.opacity = appear
        }
    }

    // Lens refraction: inside each glass shape, redraw what sits behind it
    // magnified and displaced, clipped to the exact outline of the element
    const refract = (): void => {
        snapCtx.setTransform(1, 0, 0, 1, 0, 0)
        snapCtx.globalAlpha = 1
        snapCtx.globalCompositeOperation = 'copy'
        snapCtx.drawImage(back, 0, 0)
        snapCtx.globalCompositeOperation = 'source-over'

        for (const entry of glass) {
            if (!entry.enabled || entry.opacity <= 0.01) continue

            const { def } = entry
            const shift = def.shift * layout.r
            if (def.refraction === 1 && shift === 0) continue

            const rotation = entry.rot * DEG
            const reach = (Math.hypot(entry.w, entry.h) / 2) * entry.scale + 2

            backCtx.save()
            backCtx.translate(entry.cx, entry.cy)
            backCtx.rotate(rotation)
            backCtx.scale(entry.scale, entry.scale)
            backCtx.beginPath()
            traceRoundedRect(backCtx, -entry.w / 2, -entry.h / 2, entry.w, entry.h, entry.radius)
            backCtx.setTransform(layout.scaleX, 0, 0, layout.scaleY, 0, 0)
            backCtx.clip()

            backCtx.globalCompositeOperation = 'source-over'
            backCtx.clearRect(entry.cx - reach, entry.cy - reach, reach * 2, reach * 2)
            backCtx.globalCompositeOperation = 'lighter'

            if (entry.opacity < 0.999) {
                backCtx.globalAlpha = 1 - entry.opacity
                backCtx.drawImage(snap, 0, 0, layout.width, layout.height)
            }

            backCtx.globalAlpha = entry.opacity
            backCtx.translate(entry.cx + Math.cos(rotation) * shift, entry.cy + Math.sin(rotation) * shift)
            backCtx.scale(def.refraction, def.refraction)
            backCtx.translate(-entry.cx, -entry.cy)
            backCtx.drawImage(snap, 0, 0, layout.width, layout.height)
            backCtx.restore()
        }
    }

    // Moves each element and keeps its rim light, highlight and reflection facing the light
    const writeGlass = (): void => {
        for (const entry of glass) {
            if (!entry.enabled) continue

            const { el, written } = entry

            el.style.transform = `translate3d(${(entry.cx - entry.w / 2).toFixed(2)}px, ${(entry.cy - entry.h / 2).toFixed(2)}px, 0) rotate(${entry.rot.toFixed(3)}deg) scale(${entry.scale.toFixed(4)})`

            const opacity = entry.opacity >= 0.999 ? 1 : entry.opacity

            if (Math.abs(opacity - written.opacity) > 0.004 || (opacity === 1 && written.opacity !== 1)) {
                el.style.opacity = opacity === 1 ? '1' : opacity.toFixed(3)
                written.opacity = opacity
            }

            const rim = LIGHT_ANGLE - entry.rot

            if (!(Math.abs(rim - written.rim) < 0.25)) {
                el.style.setProperty('--rim-angle', `${rim.toFixed(2)}deg`)
                el.style.setProperty('--sheen-angle', `${(122 - entry.rot).toFixed(2)}deg`)
                written.rim = rim
            }

            const rotation = entry.rot * DEG
            const cos = Math.cos(rotation)
            const sin = Math.sin(rotation)
            const reach = Math.min(entry.w, entry.h) * 0.3
            const specX = entry.w / 2 + (LIGHT_X * cos + LIGHT_Y * sin) * reach
            const specY = entry.h / 2 + (LIGHT_Y * cos - LIGHT_X * sin) * reach

            if (!(Math.abs(specX - written.specX) < 0.4 && Math.abs(specY - written.specY) < 0.4)) {
                el.style.setProperty('--spec-x', `${specX.toFixed(1)}px`)
                el.style.setProperty('--spec-y', `${specY.toFixed(1)}px`)
                written.specX = specX
                written.specY = specY
            }

            const home = layout.fx + entry.def.x * layout.r
            const sheen = clamp(40 + ((entry.cx - home) / layout.r) * 160 + (entry.rot - entry.def.rotation) * 0.9, 6, 94)

            if (!(Math.abs(sheen - written.sheen) < 0.35)) {
                el.style.setProperty('--sheen', `${sheen.toFixed(2)}%`)
                written.sheen = sheen
            }
        }
    }

    // Frame

    const prepare = (ctx: Canvas2D, canvas: HTMLCanvasElement): void => {
        ctx.setTransform(1, 0, 0, 1, 0, 0)
        ctx.globalCompositeOperation = 'source-over'
        ctx.globalAlpha = 1
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        ctx.setTransform(layout.scaleX, 0, 0, layout.scaleY, 0, 0)
        ctx.globalCompositeOperation = 'lighter'
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
    }

    const render = (still: boolean): void => {
        if (layout.width < 4 || layout.height < 4) return

        const reveal = still ? 1 : easeOutCubic(clamp(introClock / INTRO_SECONDS, 0, 1))

        updateView(still)
        updateGlass(still)
        positionNodes(still)

        for (const current of currents) {
            computeCurrent(current)
        }

        prepare(backCtx, back)
        drawAmbient(backCtx, reveal)

        for (const current of currents) {
            if (current.def.layer === 'back') drawCurrent(backCtx, current, still, reveal)
        }

        drawLinks(backCtx)

        if (!still) {
            drawSignals(backCtx)
            drawPings(backCtx)
        }

        drawNodes(backCtx)
        drawRing(backCtx, 'back')

        if (quality < 1) refract()

        prepare(frontCtx, front)

        for (const current of currents) {
            if (current.def.layer === 'front') drawCurrent(frontCtx, current, still, reveal)
        }

        drawRing(frontCtx, 'front')
        drawBokeh(frontCtx, reveal)

        writeGlass()
    }

    // Steps down gracefully on devices that cannot keep up: first the refraction
    // and the widest glow, then the canvas resolution
    const watchPerformance = (dt: number): void => {
        if (introClock < 2.5 || quality >= 2) return

        frameAverage += (dt - frameAverage) * 0.05
        slowSeconds = frameAverage > 0.034 ? slowSeconds + dt : Math.max(0, slowSeconds - dt)
        if (slowSeconds < 3) return

        quality += 1
        slowSeconds = 0
        frameAverage = 1 / 60
        if (quality >= 2) needsLayout = true
    }

    const step = (dt: number): void => {
        const ease = 1 - Math.exp(-dt * config.parallax.ease)

        time += dt
        introClock += dt
        pointer.x += (pointer.tx - pointer.x) * ease
        pointer.y += (pointer.ty - pointer.y) * ease
        ring.angle = (ring.angle + (config.ring.satelliteSpeed + config.ring.satelliteDrift * noise2D(ringSeed + 3.3, time * 0.05)) * dt) % TAU

        updateNodes(dt)
        computeLinks()
        updatePulses(dt)
        updateSignals(dt)
        updatePings(dt)
        watchPerformance(dt)
    }

    const frame = (now: number): void => {
        raf = 0
        if (!running) return

        const ratio = window.devicePixelRatio || 1

        if (ratio !== deviceRatio) {
            deviceRatio = ratio
            needsLayout = true
        }

        if (needsLayout) {
            needsLayout = false
            applyLayout()
        }

        const dt = last ? clamp((now - last) / 1000, 0, 0.05) : 1 / 60
        last = now

        step(dt)
        render(false)

        raf = window.requestAnimationFrame(frame)
    }

    const start = (): void => {
        if (running || reduced || !inView || document.hidden) return

        running = true
        last = 0
        raf = window.requestAnimationFrame(frame)
    }

    const stop = (): void => {
        running = false
        if (raf) window.cancelAnimationFrame(raf)
        raf = 0
    }

    // Reduced motion: one composed, still frame
    const renderStill = (): void => {
        time = config.staticTime
        introClock = 1000
        needsLayout = false
        applyLayout()
        computeLinks()
        render(true)
    }

    const onResize = (): void => {
        if (!running && reduced) {
            renderStill()
            return
        }

        needsLayout = true
    }

    const onPointerMove = (event: PointerEvent): void => {
        if (event.pointerType === 'touch') return

        pointer.tx = clamp((event.clientX / Math.max(1, window.innerWidth)) * 2 - 1, -1, 1)
        pointer.ty = clamp((event.clientY / Math.max(1, window.innerHeight)) * 2 - 1, -1, 1)
    }

    const onPointerReset = (): void => {
        pointer.tx = 0
        pointer.ty = 0
    }

    const onVisibility = (): void => {
        if (document.hidden) {
            stop()
            return
        }

        start()
    }

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)')

    const onMotion = (): void => {
        reduced = motion.matches

        if (reduced) {
            stop()
            renderStill()
            return
        }

        start()
    }

    let resizeObserver: ResizeObserver | null = null
    let intersection: IntersectionObserver | null = null

    if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(onResize)
        resizeObserver.observe(root)
    }
    else {
        window.addEventListener('resize', onResize)
    }

    if (typeof IntersectionObserver !== 'undefined') {
        intersection = new IntersectionObserver((entries) => {
            inView = entries.some((entry) => entry.isIntersecting)

            if (inView) {
                start()
                return
            }

            stop()
        })
        intersection.observe(root)
    }

    if (typeof motion.addEventListener === 'function') {
        motion.addEventListener('change', onMotion)
    }
    else {
        motion.addListener(onMotion)
    }

    window.addEventListener('pointermove', onPointerMove, { passive: true })
    window.addEventListener('blur', onPointerReset)
    document.documentElement.addEventListener('pointerleave', onPointerReset)
    document.addEventListener('visibilitychange', onVisibility)

    reduced = motion.matches
    applyLayout()

    for (const current of currents) {
        for (const pulse of current.pulses) {
            resetPulse(pulse, current, true)
        }
    }

    if (reduced) {
        renderStill()
    }
    else {
        start()
    }

    return {
        destroy: () => {
            stop()
            resizeObserver?.disconnect()
            intersection?.disconnect()
            window.removeEventListener('resize', onResize)
            window.removeEventListener('pointermove', onPointerMove)
            window.removeEventListener('blur', onPointerReset)
            document.documentElement.removeEventListener('pointerleave', onPointerReset)
            document.removeEventListener('visibilitychange', onVisibility)

            if (typeof motion.removeEventListener === 'function') {
                motion.removeEventListener('change', onMotion)
            }
            else {
                motion.removeListener(onMotion)
            }
        }
    }
}
